import AddLinkIcon from '@mui/icons-material/AddLink';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Alert, Box, Button, CircularProgress, Collapse, Divider, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {ProfileListItem, ProfileListStay} from 'crqlar-react-common/dist/models/profile/profileListItem.model';
import {Survey, SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';
import {SurveyInvitationStatus} from 'crqlar-react-common/dist/models/survey/surveyInvitation.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {enqueueSnackbar} from 'notistack';
import {ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';
import {useStayDates} from '../../common/guestTable/guestTableCells';
import {SURVEY_ACTIVE_INVITATION_QUERY_KEY, useSurveyInvitationRefresh} from '../shared/surveyQueryRefresh';
import SurveyInvitationLink from './SurveyInvitationLink';
import {useSurveyQrLogoUrl} from './surveyQrBranding.util';
import SurveyShareInvitationsTable from './SurveyShareInvitationsTable';
import SurveyShareSubmissionsTable from './SurveyShareSubmissionsTable';

/** The name, else the address: a contact invited by email may have no name. */
export const invitationGuestName = (profile: Pick<ProfileListItem, 'firstName' | 'lastName' | 'primaryEmail'>) =>
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.primaryEmail || '—';

/** The address under the name, unless the name line already is the address. */
export const invitationGuestEmailLine = (profile: Pick<ProfileListItem, 'firstName' | 'lastName' | 'primaryEmail'>) =>
    profile.firstName || profile.lastName ? profile.primaryEmail : undefined;

export type SurveyInvitationGuest = Pick<ProfileListItem, 'firstName' | 'lastName' | 'primaryEmail'> & {id?: string};

/** One known fact as a line — what is already decided reads as text, not as a field. */
function SummaryRow({icon, children, action}: {icon: ReactNode; children: ReactNode; action?: ReactNode}) {
    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            gap={1.5}
            sx={{minHeight: 40, px: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, color: 'text.secondary'}}
        >
            {icon}
            <Typography variant={'body1'} color={'text.primary'} noWrap sx={{flex: 1, minWidth: 0}}>{children}</Typography>
            {action}
        </Stack>
    );
}

interface Props {
    guest: SurveyInvitationGuest
    stay?: ProfileListStay
    /** The guest's other stays, when the entry point leaves the stay open; offered behind "Change". */
    stays?: ProfileListStay[]
    onStayChange?: (reservationId: string) => void
    survey?: Survey
    /** The survey picker, when the entry point does not know the survey. */
    surveyField?: ReactNode
}

/**
 * A guest's invitation to one survey: the stay and the survey as lines, then the one thing to do — create the link,
 * or share the one that exists. Earlier invitations and answers stay folded until asked for.
 */
export default function SurveyInvitationDetails({guest, stay, stays, onStayChange, survey, surveyField}: Props) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const refresh = useSurveyInvitationRefresh();
    const logoUrl = useSurveyQrLogoUrl(survey);
    const stayDates = useStayDates();
    const [changingStay, setChangingStay] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);

    const profileId = guest.id || undefined;
    const reservationId = stay?.reservationId;
    const target = {surveyId: survey?.id, profileId, reservationId};
    const invitationQuery = useQuery(
        [SURVEY_ACTIVE_INVITATION_QUERY_KEY, target],
        () => SurveyService.invitationList({
            ...target,
            withoutReservation: !reservationId,
            statuses: Object.values(SurveyInvitationStatus).filter(status => status !== SurveyInvitationStatus.VOIDED),
            limit: {offset: 0, limit: 1, sort: [{field: 'sentAt', direction: 'DESC'}]}
        }),
        {enabled: !!survey?.id && (!!profileId || !!reservationId), refetchOnWindowFocus: false}
    );
    const invitation = invitationQuery.data?.content?.[0];
    const pending = busy || (!!survey?.id && invitationQuery.isLoading);
    const historyScope = profileId ? {profileId} : {reservationId};

    const stayLabel = (candidate?: ProfileListStay) => {
        if (!candidate?.reservationId) return t('No reservation');
        const dates = stayDates(candidate.startDay, candidate.endDay);
        return [candidate.roomId && `${t('Room')} ${candidate.roomId}`, dates].filter(Boolean).join(' · ') || '—';
    };

    const createLink = async () => {
        if (readOnly || busy || !survey?.id || (!profileId && !reservationId)) return;
        setBusy(true);
        setFailed(false);
        try {
            await SurveyService.invitationCreate({surveyId: survey.id, profileId, reservationId});
            await refresh();
            enqueueSnackbar(t('New invitation link created'), {variant: 'success'});
        } catch {
            setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap={2}>
            {changingStay && stays ? (
                <TextField
                    select
                    fullWidth
                    size={'small'}
                    label={t('Stay')}
                    value={reservationId ?? ''}
                    onChange={event => {
                        setChangingStay(false);
                        onStayChange?.(event.target.value);
                    }}
                >
                    <MenuItem value={''}>{t('No reservation')}</MenuItem>
                    {stays.map(candidate => (
                        <MenuItem key={candidate.reservationId} value={candidate.reservationId}>{stayLabel(candidate)}</MenuItem>
                    ))}
                </TextField>
            ) : (
                <SummaryRow
                    icon={<BedOutlinedIcon fontSize={'small'}/>}
                    action={!!stays?.length && onStayChange && (
                        <Button size={'small'} onClick={() => setChangingStay(true)}>{t('Change', {context: 'action'})}</Button>
                    )}
                >
                    {stayLabel(stay)}
                </SummaryRow>
            )}

            {surveyField ?? (
                <SummaryRow
                    icon={<AssignmentOutlinedIcon fontSize={'small'}/>}
                    // Says why Create link is off, as the survey select marks drafts.
                    action={survey && survey.status !== SurveyStatus.PUBLISHED && (
                        <Typography variant={'body2'} color={'error.main'} sx={{flexShrink: 0}}>{t('Draft')}</Typography>
                    )}
                >
                    {survey?.name || '—'}
                </SummaryRow>
            )}

            {failed && <Alert severity={'error'}>{t('This link could not be created.')}</Alert>}
            {invitationQuery.isError && (
                <Alert severity={'error'} action={<Button onClick={() => void invitationQuery.refetch()}>{t('Retry')}</Button>}>
                    {t('This link could not be loaded.')}
                </Alert>
            )}

            <Box sx={{pt: 1}}>
                {invitation ? <SurveyInvitationLink key={invitation.id} invitation={invitation} logoUrl={logoUrl}/> : (
                    <Button
                        variant={'contained'}
                        disableElevation
                        startIcon={pending ? <CircularProgress size={16} color={'inherit'}/> : <AddLinkIcon/>}
                        disabled={readOnly || pending || invitationQuery.isError || survey?.status !== SurveyStatus.PUBLISHED
                            || (!profileId && !reservationId)}
                        onClick={createLink}
                    >
                        {t('Create link')}
                    </Button>
                )}
            </Box>

            <Divider sx={{mt: 1}}/>
            <Box>
                <Button
                    color={'inherit'}
                    aria-expanded={historyOpen}
                    onClick={() => setHistoryOpen(open => !open)}
                    startIcon={<ExpandMoreIcon sx={{transform: historyOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms'}}/>}
                    sx={{ml: -1, color: 'text.secondary'}}
                >
                    {t('Earlier invitations and answers')}
                </Button>
                {/* Loads only once opened: most links are made without looking back. */}
                <Collapse in={historyOpen} unmountOnExit>
                    <Stack gap={2} sx={{pt: 1.5}}>
                        <Stack gap={1}>
                            <Typography variant={'subtitle2'}>
                                {t(profileId ? 'Guest invitations' : 'Invitations for this reservation')}
                            </Typography>
                            <SurveyShareInvitationsTable {...historyScope}/>
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant={'subtitle2'}>
                                {t(profileId ? 'Guest submissions' : 'Submissions for this reservation')}
                            </Typography>
                            <SurveyShareSubmissionsTable {...historyScope}/>
                        </Stack>
                    </Stack>
                </Collapse>
            </Box>
        </Stack>
    );
}
